// Top-level finding dispatcher + per-metric wrappers + evidence-flight
// helpers, extracted from app.js. This is the layer between the engine
// review summary and the rendered finding card UI:
//
//   metrics  -> buildFinding(metrics)     // picks worst metric → finding
//   summary  -> findingForMetric(key)     // dispatches by health-card key
//
// Each per-metric wrapper (chtFinding, valveFinding, ...) closes over the
// shared "healthy metric evidence context" so callers do not need to
// rebuild it. The evidence-flight helpers (sensorEvidenceFlights,
// valveEvidenceFlights, etc.) read from the cross-flight trend cache for
// the selected aircraft to surface related-flight references.
//
// Exposed as a factory: app.js destructures the returned wrappers and
// dispatcher under the same names the rest of the app already uses.

import { metricParticipatesInScopedScore, noDataSummaryFinding } from "../engine/review-model.js";

export function createFindingDispatch({
  // state getters
  getDetectionSensitivity,
  getSelectedFlight,
  getTrendCache,
  getFlightManifest,
  // basic helpers
  titleCase,
  normalizeSensitivity,
  sensitivityModes,
  // engine helpers
  currentEngineDescriptors,
  engineNarrativeProbeLabel,
  engineScopedProbeLabel,
  engineInfoForCylinderNumber,
  engineScopedCylinderLabel,
  // metric / format helpers
  metricDisplayClass,
  valveBandLabel,
  // sensor summary
  sensorReviewSummaries,
  // trend cache helpers
  trendCacheKey,
  staticValveRowsForAircraft,
  // FromContext finding builders (imports from src/findings/...)
  healthyMetricEvidenceFromContext,
  healthyMetricFindingFromContext,
  chtFindingFromContext,
  chtRhythmFindingFromContext,
  egtFindingFromContext,
  combustionFindingFromContext,
  abnormalCombustionFindingFromContext,
  valveFindingFromContext,
  descentEgtDropoutFindingFromContext,
  magFindingFromContext,
  coolingFindingFromContext,
  inductionLeakFindingFromContext,
  coolingRateFindingFromContext,
  turboFindingFromContext,
  turboMetricEvidenceRowsFromContext,
  findingCylinderListLabelFromContext,
  // Direct finding builders (no context wrapper needed)
  sensorFinding,
  oilSystemFinding,
  coolantMarginFinding,
  takeoffEnrichmentFinding,
  carbIceFinding,
  startChargeFinding,
  crossEngineComparisonFinding,
  electricalFinding,
  instrumentationSuspicionFinding,
}) {
  function noMajorCueFinding(sensitivity) {
    const mode = sensitivityModes[sensitivity];
    return {
      title: `No major engine review cue at ${mode.label} sensitivity`,
      meta: "Rule-based first pass",
      body: "The evaluated checks did not produce a major engine review cue at this sensitivity setting. Checks without enough usable data are unscored, so this result covers only the available evidence. This is automated advisory screening, not a maintenance diagnosis.",
      causes: [
        ["Continue trend monitoring", 0.55],
        ["Compare against future flights", 0.3],
        ["Review raw traces for context", 0.15],
      ],
      actions: [
        ["low", "Use this flight as a comparison baseline"],
        ["low", "Keep collecting logs from similar power settings"],
      ],
      confidence: "Medium",
    };
  }

  function buildFinding(metrics: Record<string, any>, options: Record<string, any> = {}) {
    const sensitivity = normalizeSensitivity(options.sensitivity ?? getDetectionSensitivity());
    const disabledMetricKeys = new Set(Array.isArray(options.disabledMetricKeys) ? options.disabledMetricKeys : []);
    const metricEnabled = (key) => !disabledMetricKeys.has(key) && metrics[key]?.details?.status !== "not-applicable";
    // Operating-context metrics (sensorCoherence, thermalContext, fuelFlowResponse,
    // etc.) are not primary findings — they surface as context cards. Excluding them
    // here keeps `worst` a finding-eligible metric so the key dispatch below always
    // matches a real case instead of falling through to electricalFinding. This matters
    // for the per-engine PDF re-analysis, where projected single-engine data drives some
    // context metrics very low (e.g. sensorCoherence). `healthMetricScopeForMetric` keeps
    // mag/charging eligible when they carry a real scored finding.
    const ranked = Object.values(metrics)
      .filter((metric) => metric && metricEnabled(metric.key) && metricParticipatesInScopedScore(metric.key, metric))
      .sort((a, b) => a.score - b.score);
    if (!ranked.length) return noDataSummaryFinding();
    const worst = ranked[0];
    const instrumentationSuspicion = metrics.instrumentationSuspicion;
    if (metricEnabled("instrumentationSuspicion") && Number.isFinite(instrumentationSuspicion?.score) && instrumentationSuspicion.score < 72) {
      return instrumentationSuspicionFinding(instrumentationSuspicion);
    }
    const sensors = metrics.sensors;
    if (metricEnabled("sensors") && Number.isFinite(sensors?.score) && sensors.score < 72) {
      return sensorFinding(sensors);
    }
    const oilSystem = metrics.oilSystem;
    if (metricEnabled("oilSystem") && Number.isFinite(oilSystem?.score) && oilSystem.score < 72) {
      return oilSystemFinding(oilSystem);
    }
    const coolantMargin = metrics.coolantMargin;
    if (metricEnabled("coolantMargin") && Number.isFinite(coolantMargin?.score) && coolantMargin.score < 72) {
      return coolantMarginFinding(coolantMargin);
    }
    const takeoffEnrichment = metrics.takeoffEnrichment;
    if (metricEnabled("takeoffEnrichment") && Number.isFinite(takeoffEnrichment?.score) && takeoffEnrichment.score < 72) {
      return takeoffEnrichmentFinding(takeoffEnrichment);
    }
    // Detonation/pre-ignition is fast-damaging, so surface a flagged abnormal-combustion
    // cue as the primary finding ahead of slower-developing review metrics.
    const abnormalCombustion = metrics.abnormalCombustion;
    if (metricEnabled("abnormalCombustion") && Number.isFinite(abnormalCombustion?.score) && abnormalCombustion.score < 72) {
      return abnormalCombustionFinding(abnormalCombustion);
    }
    const inductionLeak = metrics.inductionLeak;
    if (metricEnabled("inductionLeak") && Number.isFinite(inductionLeak?.score) && inductionLeak.score < 72) {
      return inductionLeakFinding(inductionLeak);
    }
    const turbo = metrics.turbo;
    if (metricEnabled("turbo") && Number.isFinite(turbo?.score) && turbo.score < 72) {
      return turboFinding(turbo);
    }
    const mag = metrics.mag;
    if (metricEnabled("mag") && Number.isFinite(mag?.score) && mag.score < 58) {
      return magFinding(mag);
    }
    const carbIce = metrics.carbIce;
    if (metricEnabled("carbIce") && Number.isFinite(carbIce?.score) && carbIce.score < 72) {
      return carbIceFinding(carbIce);
    }
    const coolingRate = metrics.coolingRate;
    if (metricEnabled("coolingRate") && Number.isFinite(coolingRate?.score) && coolingRate.score < 72) {
      return coolingRateFinding(coolingRate);
    }
    const charging = metrics.charging;
    if (metricEnabled("charging") && Number.isFinite(charging?.score) && charging.score < 72) {
      return startChargeFinding(charging);
    }
    const engineDeltaMetric = metrics.engineDelta;
    if (metricEnabled("engineDelta") && Number.isFinite(engineDeltaMetric?.score) && engineDeltaMetric.score < 72) {
      return crossEngineComparisonFinding(engineDeltaMetric);
    }
    const valve = metrics.valve;
    if (metricEnabled("valve") && Number.isFinite(valve?.score) && (valve.score < 50 || (valve.score < 72 && (!worst || valve.score <= worst.score + 20)))) {
      return valveFinding(valve);
    }
    if (!worst || worst.score >= 88) {
      return noMajorCueFinding(sensitivity);
    }
    if (worst.key === "cht") return chtFinding(worst);
    if (worst.key === "chtRhythm") return chtRhythmFinding(worst);
    if (worst.key === "sensors") return sensorFinding(worst);
    if (worst.key === "turbo") return turboFinding(worst);
    if (worst.key === "oilSystem") return oilSystemFinding(worst);
    if (worst.key === "coolantMargin") return coolantMarginFinding(worst);
    if (worst.key === "takeoffEnrichment") return takeoffEnrichmentFinding(worst);
    if (worst.key === "egt") return egtFinding(worst);
    if (worst.key === "valve") return valveFinding(worst);
    if (worst.key === "abnormalCombustion") return abnormalCombustionFinding(worst);
    if (worst.key === "inductionLeak") return inductionLeakFinding(worst);
    if (worst.key === "mag") return magFinding(worst);
    if (worst.key === "carbIce") return carbIceFinding(worst);
    if (worst.key === "cooling") return coolingFinding(worst);
    if (worst.key === "coolingRate") return coolingRateFinding(worst);
    if (worst.key === "charging") return startChargeFinding(worst);
    if (worst.key === "engineDelta") return crossEngineComparisonFinding(worst);
    if (worst.key === "instrumentationSuspicion") return instrumentationSuspicionFinding(worst);
    if (worst.key === "electrical") return electricalFinding(worst);
    // `worst` is finding-eligible but has no dedicated card (or a future metric slipped
    // through); never assume electrical — fall back to the generic no-cue finding.
    return noMajorCueFinding(sensitivity);
  }

  function findingForMetric(key, summary) {
    if (!summary || key === "overall") return summary?.finding ?? null;
    const metric = summary[key];
    if (!metric) return summary.finding;
    if (!Number.isFinite(metric.score)) return unavailableMetricFinding(key, metric);
    if (key === "sensors" && metric.score >= 88) return healthyMetricFinding(key, metric);
    if (key === "turbo" && ["limit-review", "data-quality-review"].includes(metric.details?.status)) return turboFinding(metric);
    if (key === "sensors" && sensorReviewSummaries(metric).length) return sensorFinding(metric);
    if (key === "egt" && metric.details?.sustainedLowEgt) return egtFinding(metric);
    if (metric.score >= 88) return healthyMetricFinding(key, metric);
    if (key === "sensors") return sensorFinding(metric);
    if (key === "turbo") return turboFinding(metric);
    if (key === "oilSystem") return oilSystemFinding(metric);
    if (key === "coolantMargin") return coolantMarginFinding(metric);
    if (key === "takeoffEnrichment") return takeoffEnrichmentFinding(metric);
    if (key === "cht") return chtFinding(metric);
    if (key === "chtRhythm") return chtRhythmFinding(metric);
    if (key === "egt") return egtFinding(metric);
    if (key === "valve") return valveFinding(metric);
    if (key === "abnormalCombustion") return abnormalCombustionFinding(metric);
    if (key === "inductionLeak") return inductionLeakFinding(metric);
    if (key === "mag") return magFinding(metric);
    if (key === "carbIce") return carbIceFinding(metric);
    if (key === "cooling") return coolingFinding(metric);
    if (key === "coolingRate") return coolingRateFinding(metric);
    if (key === "charging") return startChargeFinding(metric);
    if (key === "engineDelta") return crossEngineComparisonFinding(metric);
    if (key === "electrical") return electricalFinding(metric);
    if (key === "instrumentationSuspicion") return instrumentationSuspicionFinding(metric);
    return summary.finding;
  }

  function descentEgtDropoutHasCandidate(metric) {
    return Number.isFinite(metric?.details?.top?.dropoutF)
      && Number.isFinite(metric?.details?.top?.startTime)
      && Number.isFinite(metric?.details?.top?.suspicion);
  }

  function healthyMetricEvidenceContext() {
    return {
      readableProbeLabel,
      currentEngineDescriptors,
      findingCylinderReference,
      capitalizeSentence,
    };
  }

  function healthyMetricEvidence(key, metric) {
    return healthyMetricEvidenceFromContext(key, metric, healthyMetricEvidenceContext());
  }

  function healthyMetricFinding(key, metric) {
    return healthyMetricFindingFromContext(key, metric, healthyMetricEvidenceContext());
  }

  function metricDisplayName(key) {
    return {
      sensors: "sensor health",
      oilSystem: "oil system",
      coolantMargin: "coolant margin",
      takeoffEnrichment: "takeoff fuel flow",
      cht: "CHT heat",
      chtRhythm: "CHT rhythm",
      egt: "EGT balance",
      valve: "EGT rhythm",
      combustion: "CHT/EGT pairing",
      abnormalCombustion: "abnormal combustion",
      divergence: "transient divergence",
      inductionLeak: "induction leak",
      mag: "mag check",
      turbo: "turbo/boost",
      carbIce: "carb ice",
      cooling: "cooling",
      coolingRate: "cooling rate",
      charging: "start charge",
      electrical: "electrical",
    }[key] ?? "metric";
  }

  function unavailableMetricFinding(key, metric) {
    const name = metricDisplayName(key);
    if (key === "turbo" && metric?.details?.available) return turboFinding(metric);
    const missingRpmMagCheck = key === "mag" && /rpm/i.test(metric?.note ?? "");
    if (missingRpmMagCheck) {
      return {
        title: "Mag check unavailable — no RPM channel",
        meta: "This monitor records no engine RPM",
        body: "A mag check can't be evaluated on this log because this engine monitor doesn't record RPM. A mag check is confirmed mainly from the RPM drop when each magneto is selected; without an RPM channel the app can't measure that drop, even when the mag check was flown. The EGT rise alone isn't enough to score it reliably.",
        causes: [
          ["This engine monitor has no RPM/tach channel", 1],
        ],
        actions: [
          ["low", "No action needed — keep confirming the mag check on the tachometer during runup, as usual"],
          ["low", "An RPM-equipped monitor would let the app score the mag check automatically"],
        ],
        confidence: "High",
        weightedCauses: false,
      };
    }
    return {
      title: `${titleCase(name)} unavailable`,
      meta: metric?.note ?? "No score for this log",
      body: `This log does not include enough usable ${name} data to explain that card. The app keeps the card visible so channel availability is obvious when comparing logs from different monitors.`,
      causes: [
        ["Channel not present in this monitor export", 0.45],
        ["Not enough comparable samples", 0.32],
        ["Sensor values filtered as missing or invalid", 0.23],
      ],
      actions: [
        ["medium", "Check the Raw Data channel inventory for this log"],
        ["low", "Compare with a log that includes the missing channel"],
      ],
      confidence: "High",
    };
  }

  function turboMetricEvidenceRows(metric) {
    return turboMetricEvidenceRowsFromContext(metric, { metricDisplayClass });
  }

  function turboFinding(metric) {
    return turboFindingFromContext(metric, { metricDisplayClass });
  }

  function relatedTrendRowsForSelectedAircraft() {
    const aircraft = getSelectedFlight()?.aircraft;
    if (!aircraft) return [];
    return (getTrendCache().get(trendCacheKey(aircraft)) ?? []).filter((row) => !row.error);
  }

  function sortEvidenceReferences(items) {
    return items.sort((a, b) => {
      if (a.isCurrent !== b.isCurrent) return Number(b.isCurrent) - Number(a.isCurrent);
      return (a.score ?? 100) - (b.score ?? 100) || (b.rank ?? 0) - (a.rank ?? 0) || a.title.localeCompare(b.title);
    }).slice(0, 6);
  }

  function sensorEvidenceNote(row) {
    const parts = [];
    if (row.sensorImpossibleJumpCount) parts.push(`${row.sensorImpossibleJumpCount.toLocaleString()} abrupt data jump${row.sensorImpossibleJumpCount === 1 ? "" : "s"}`);
    if (row.sensorOutOfRangeCount) parts.push(`${row.sensorOutOfRangeCount.toLocaleString()} out-of-range sample${row.sensorOutOfRangeCount === 1 ? "" : "s"}`);
    if (row.sensorZeroCount) parts.push(`${row.sensorZeroCount.toLocaleString()} zero/dropout sample${row.sensorZeroCount === 1 ? "" : "s"}`);
    if (row.sensorPeerMismatchCount) parts.push(`${row.sensorPeerMismatchCount.toLocaleString()} peer-mismatch sample${row.sensorPeerMismatchCount === 1 ? "" : "s"}`);
    return parts.length ? parts.slice(0, 2).join(" · ") : "Probe-quality pattern repeated";
  }

  function sensorEvidenceFlights(sensorKey) {
    if (!sensorKey) return [];
    const selectedFlight = getSelectedFlight();
    return sortEvidenceReferences(relatedTrendRowsForSelectedAircraft()
      .filter((row) => row.sensorKey === sensorKey && Number.isFinite(row.sensorScore) && row.sensorScore < 88)
      .map((row) => ({
        id: row.id,
        title: row.title,
        note: sensorEvidenceNote(row),
        score: row.sensorScore,
        rank: row.sensorSuspicion ?? 0,
        isCurrent: selectedFlight?.id === row.id,
      })));
  }

  function coolingEvidenceFlights(cylinder) {
    if (!Number.isFinite(cylinder)) return [];
    const selectedFlight = getSelectedFlight();
    return sortEvidenceReferences(relatedTrendRowsForSelectedAircraft()
      .filter((row) => row.coolingCylinder === cylinder && Number.isFinite(row.coolingScore) && row.coolingScore < 88)
      .map((row) => ({
        id: row.id,
        title: row.title,
        note: `${findingCylinderReference(cylinder).label} ${Math.round(row.coolingP95Delta ?? 0)} F hotter than other cylinders${Number.isFinite(row.coolingP95Cht) ? `, high CHT about ${Math.round(row.coolingP95Cht)} F` : ""}`,
        score: row.coolingScore,
        rank: row.coolingP95Delta ?? 0,
        isCurrent: selectedFlight?.id === row.id,
      })));
  }

  function coolingRateEvidenceFlights(cylinder) {
    const selectedFlight = getSelectedFlight();
    return sortEvidenceReferences(relatedTrendRowsForSelectedAircraft()
      .filter((row) => {
        if (!Number.isFinite(row.coolingRateScore) || row.coolingRateScore >= 88) return false;
        return !Number.isFinite(cylinder) || row.coolingRateCylinder === cylinder;
      })
      .map((row) => ({
        id: row.id,
        title: row.title,
        note: `${Number.isFinite(row.coolingRateCylinder) ? `${findingCylinderReference(row.coolingRateCylinder).label} ` : ""}${Math.round(row.coolingRateFpm ?? 0)} F/min max CHT cooling`,
        score: row.coolingRateScore,
        rank: row.coolingRateFpm ?? 0,
        isCurrent: selectedFlight?.id === row.id,
      })));
  }

  function chtFinding(metric) {
    return chtFindingFromContext(metric, healthyMetricEvidenceContext());
  }

  function readableProbeLabel(label) {
    return (engineNarrativeProbeLabel(label) || engineScopedProbeLabel(label, currentEngineDescriptors()) || "").trim();
  }

  function chtRhythmFinding(metric) {
    return chtRhythmFindingFromContext(metric, {
      ...healthyMetricEvidenceContext(),
      readableProbeLabel,
    });
  }

  function egtFinding(metric) {
    return egtFindingFromContext(metric, healthyMetricEvidenceContext());
  }

  function combustionFinding(metric) {
    return combustionFindingFromContext(metric, healthyMetricEvidenceContext());
  }

  function abnormalCombustionFinding(metric) {
    return abnormalCombustionFindingFromContext(metric, healthyMetricEvidenceContext());
  }

  function findingCylinderReference(cylinder) {
    if (!Number.isFinite(cylinder)) return { label: "this cylinder", location: "" };
    const engines = currentEngineDescriptors();
    const info = engineInfoForCylinderNumber(cylinder, engines);
    if (engines.length > 1 && info.engine && info.cylinder) {
      const engineLabel = String(info.engine.label ?? info.engine.shortLabel ?? "Engine").replace(/\s+Engine$/i, "");
      const label = `${engineLabel} Cyl ${info.cylinder.number}`;
      return {
        label,
        location: ` on ${label}`,
      };
    }
    const label = `cylinder #${cylinder}`;
    return {
      label,
      location: ` on ${label}`,
    };
  }

  function capitalizeSentence(value) {
    const text = String(value ?? "");
    return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
  }

  function findingCylinderListLabel(cylinders) {
    return findingCylinderListLabelFromContext(cylinders, { findingCylinderReference });
  }

  function valveFinding(metric) {
    return valveFindingFromContext(metric, { ...healthyMetricEvidenceContext(), valveEvidenceFlights });
  }

  function descentEgtDropoutFinding(metric) {
    return descentEgtDropoutFindingFromContext(metric, healthyMetricEvidenceContext());
  }

  function magFinding(metric) {
    return magFindingFromContext(metric, healthyMetricEvidenceContext());
  }

  function valveEvidenceFlights(cylinder) {
    const selectedFlight = getSelectedFlight();
    const aircraft = selectedFlight?.aircraft;
    if (!aircraft) return [];
    const rowMap = new Map();
    relatedTrendRowsForSelectedAircraft()
      .filter((row) => row.valveCylinder === cylinder && Number.isFinite(row.valveScore) && row.valveScore < 88)
      .forEach((row) => {
        const band = row.valveBand && row.valveBand !== "unclassified" ? `${valveBandLabel(row.valveBand)}, ` : "";
        const frequency = Number.isFinite(row.valveFrequencyCpm) ? `, ${row.valveFrequencyCpm.toFixed(1)} cpm` : "";
        const cylinderRef = findingCylinderReference(cylinder);
        rowMap.set(row.id, {
          id: row.id,
          title: row.title,
          note: `${cylinderRef.label} ${band}${Math.round(row.valveAmplitude ?? 0)} F${frequency}`,
          score: row.valveScore,
          rank: row.valveAmplitude ?? 0,
          isCurrent: selectedFlight?.id === row.id,
        });
      });
    staticValveRowsForAircraft(selectedFlight)
      .filter((row) => row.valveCylinder === cylinder && Number.isFinite(row.valveScore) && row.valveScore < 88)
      .forEach((row) => {
        if (rowMap.has(row.id)) return;
        const entry = getFlightManifest().find((flight) => flight.id === row.id);
        rowMap.set(row.id, {
          id: row.id,
          title: entry?.title ?? row.id,
          note: row.valveNote,
          score: row.valveScore,
          rank: 0,
          isCurrent: selectedFlight?.id === row.id,
        });
      });
    return [...rowMap.values()]
      .sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent) || (a.score ?? 100) - (b.score ?? 100) || (b.rank ?? 0) - (a.rank ?? 0) || a.title.localeCompare(b.title))
      .slice(0, 6);
  }

  function coolingFinding(metric) {
    return coolingFindingFromContext(metric, { ...healthyMetricEvidenceContext(), coolingEvidenceFlights });
  }

  function inductionLeakFinding(metric) {
    return inductionLeakFindingFromContext(metric, healthyMetricEvidenceContext());
  }

  function coolingRateFinding(metric) {
    return coolingRateFindingFromContext(metric, {
      ...healthyMetricEvidenceContext(),
      currentEngineDescriptors,
      engineScopedCylinderLabel,
      metricDisplayClass,
      coolingRateEvidenceFlights,
    });
  }

  return {
    buildFinding,
    findingForMetric,
    descentEgtDropoutHasCandidate,
    healthyMetricEvidenceContext,
    healthyMetricEvidence,
    healthyMetricFinding,
    metricDisplayName,
    unavailableMetricFinding,
    turboMetricEvidenceRows,
    turboFinding,
    relatedTrendRowsForSelectedAircraft,
    sortEvidenceReferences,
    sensorEvidenceNote,
    sensorEvidenceFlights,
    coolingEvidenceFlights,
    coolingRateEvidenceFlights,
    chtFinding,
    readableProbeLabel,
    egtFinding,
    combustionFinding,
    abnormalCombustionFinding,
    findingCylinderReference,
    capitalizeSentence,
    findingCylinderListLabel,
    valveFinding,
    descentEgtDropoutFinding,
    magFinding,
    valveEvidenceFlights,
    coolingFinding,
    inductionLeakFinding,
    coolingRateFinding,
  };
}
